import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { useLang } from '@/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

let scriptPromise;
function loadGoogle() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!scriptPromise) scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = resolve;
    script.onerror = () => { script.remove(); scriptPromise = null; reject(new Error('load')); };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export function GoogleSignIn({ intent = 'login', registrationPath = 'citizen', onSuccess }) {
  const { lang } = useLang();
  const { googleLogin } = useAuth();
  const it = lang === 'it';
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(null);
  const [password, setPassword] = useState('');
  const mount = useRef(null);
  const active = useRef(true);
  const current = useRef({ onSuccess, registrationPath, intent });
  current.current = { onSuccess, registrationPath, intent };
  useEffect(() => {
    active.current = true;
    api.get('/auth/google/config').then(r => { if (active.current) setEnabled(r.data.enabled); }).catch(() => {});
    return () => { active.current = false; window.google?.accounts?.id?.cancel(); };
  }, []);

  const complete = async (payload) => {
    setBusy(true); setError('');
    try {
      const data = await googleLogin(payload);
      if (active.current) { setPending(null); setPassword(''); current.current.onSuccess(data); }
    } catch (err) {
      if (!active.current) return;
      const detail = err.response?.data?.detail;
      if (detail === 'google_link_password_required') {
        setPending(payload);
      } else if (detail === 'google_registration_required') {
        setError(it ? 'Non hai ancora un account: scegli Registrati e continua con Google.' : 'No account yet: choose Register and continue with Google.');
      } else {
        setError(it ? 'Accesso non riuscito. Controlla la password, se richiesta, oppure riprova con Google.' : 'Sign-in failed. Check your password if requested, or try Google again.');
      }
    } finally { if (active.current) setBusy(false); }
  };

  const start = async () => {
    setBusy(true); setError(''); setPending(null); setPassword('');
    try {
      const { data } = await api.post('/auth/google/challenge');
      await loadGoogle();
      if (!active.current) return;
      window.google.accounts.id.initialize({ client_id: data.client_id, nonce: data.nonce, auto_select: false, ux_mode: 'popup',
        callback: response => complete({ credential: response.credential, challenge: data.challenge,
          intent: current.current.intent, registration_path: current.current.registrationPath }) });
      mount.current.replaceChildren();
      window.google.accounts.id.renderButton(mount.current, { type: 'standard', theme: 'outline', size: 'large', text: 'continue_with', locale: lang, width: 260 });
    } catch (_) {
      if (active.current) setError(it ? 'Google non è disponibile. Puoi usare email e password.' : 'Google is unavailable. You can use email and password.');
    } finally { if (active.current) setBusy(false); }
  };
  if (!enabled) return null;
  return <div className="mt-6 pt-5 border-t border-[#E2DACB] space-y-3">
    <Button type="button" variant="outline" className="w-full" disabled={busy} onClick={start}>
      {busy ? (it ? 'Accesso in corso…' : 'Signing in…') : (it ? 'Usa Google' : 'Use Google')}
    </Button>
    <p className="text-xs text-[#6E6258]">{it ? 'Facoltativo. Scegliendo Google viene caricato il suo servizio di accesso. Riceviamo nome, email e identificativo; mai la password Google.' : 'Optional. Choosing Google loads its sign-in service. We receive your name, email and identifier; never your Google password.'}</p>
    <div ref={mount} className={busy ? 'pointer-events-none opacity-60' : ''} />
    {pending && <form className="space-y-3" onSubmit={e => { e.preventDefault(); complete({ ...pending, password }); }}>
      <label className="text-sm block">{it ? 'Hai già un account. Per collegarlo, conferma la password di PlantAdopting (non quella Google).' : 'You already have an account. To link it, confirm your PlantAdopting password (not your Google password).'}
        <Input type="password" autoComplete="current-password" required maxLength={512} value={password} onChange={e => setPassword(e.target.value)} />
      </label>
      <Button type="submit" disabled={busy}>{it ? 'Collega e accedi' : 'Link and sign in'}</Button>
    </form>}
    {error && <p role="alert" className="text-sm text-[#B3261E]">{error}</p>}
  </div>;
}
